import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  params, string, shape, arrayOf
} from '@thd-nucleus/data-sources';
import { SkeletonLine } from '@one-thd/sui-atomic-components';
import { useBreadcrumbData } from '../useBreadcrumbData';
import { BreadcrumbItem } from './BreadcrumbItem';
import {
  hasNoCategoryDimension,
  shouldHideOnNoCategory,
  getBreadcrumbsFromData,
  getSearchKeyword,
  getBrand,
  buildCrumbs
} from '../utils';

const Breadcrumbs = ({
  collectionId = null,
  componentClass = '',
  componentId = '',
  componentPosition = 1,
  data: controlledData = undefined,
  itemId = null,
  keyword: propKeyword = null,
  loading: controlledLoading = undefined,
  navParam: propNavParam = null,
  rounded = false,
  shouldLastCrumbBeLinkableOverride = true,
  shouldShowEventOnly = false,
  showInternet = false,
  staticCrumbs = null,
}) => {
  const { channel, customContext, path: experiencePath } = useContext(ExperienceContext);
  const [isBreadcrumbsExpanded, setIsBreadcrumbsExpanded] = useState(false);

  const isMobile = channel === 'mobile';
  const pathname = experiencePath || customContext?.path || '';

  const { variables } = customContext || {};
  const { navParam: contextNavParam = null, keyword: contextKeyword = null } = variables || {};
  const keyword = propKeyword || contextKeyword;
  const navParam = propNavParam || contextNavParam;

  const isControlledData = controlledData !== undefined
    || controlledLoading !== undefined
    || staticCrumbs !== null;

  // If context overrides exist and !isControlledData, use full context variables
  const searchVariables = (contextNavParam || contextKeyword) && !isControlledData
    ? { ...variables }
    : { keyword, navParam };

  const { data, loading } = useBreadcrumbData({
    itemId,
    collectionId,
    controlledData,
    controlledLoading,
    searchVariables,
    isControlledData
  });

  const breadcrumbs = getBreadcrumbsFromData(data, isMobile, shouldShowEventOnly);
  const dataKeyword = getSearchKeyword(data);
  const brand = getBrand(data);

  const crumbsResult = data || staticCrumbs
    ? buildCrumbs({
      breadcrumbs,
      itemId,
      keyword: dataKeyword,
      brand,
      shouldLastCrumbBeLinkableOverride,
      pathname,
      staticCrumbs,
      isMobile,
      shouldShowEventOnly,
      showInternet
    })
    : null;

  const items = crumbsResult?.items;

  useEffect(() => {
    if (items && window.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('breadcrumbs.ready', { crumbs: items });
    }
  }, [items]);

  if (loading && !data && !shouldShowEventOnly) {
    return (
      <div
        className="sui-grid sui-grid-cols-2 sm:sui-grid-cols-4 sui-gap-4 sui-w-full"
        data-component="BreadcrumbsPlaceholder"
      >
        {Array.from({ length: isMobile ? 2 : 4 }).map((_item, index) => (
          <SkeletonLine
            key={`breadcrumb-placeholder-${index}`}
            variant="single"
          />
        ))}
      </div>
    );
  }

  if (!crumbsResult) {
    return null;
  }

  if (shouldHideOnNoCategory({ isMobile, experiencePath, customContext }) && hasNoCategoryDimension(breadcrumbs)) {
    return null;
  }

  const shouldLimitList = isMobile ? items?.length > 2 : items?.length > 4;

  return (
    <div
      className="sui-flex sui-flex-nowrap"
      data-component-id={componentId}
      data-component-name={componentClass}
      data-component-position={componentPosition}
      data-component="Breadcrumbs"
    >
      {shouldLimitList && (
        <button
          type="button"
          className={isBreadcrumbsExpanded ? 'sui-hidden' : null}
          onClick={() => setIsBreadcrumbsExpanded(true)}
        >{'\u00A0...\u00A0'}
        </button>
      )}
      <div className={classNames(
        'sui-relative',
        'sui-z-1',
        'sui-overflow-x-auto',
        'sui-whitespace-nowrap',
        'sui-pr-2',
        '[&>*:nth-last-child(n+5)]:sui-hidden',
        'sui-pb-1', {
          ['sui-pb-2 sui-bg-primary sui--mt-1 sm:sui-mt-0 '
          + 'sui-rounded-t-none sui-rounded-b-md sm:sui-rounded-b-none '
          + 'sui-z-0 sm:sui-z-auto']: rounded,
          '[&>*:nth-last-child(n+3)]:sui-hidden': isMobile,
          '[&>*:nth-last-child(n+3)]:!sui-inline': isBreadcrumbsExpanded
        }
      )}
      >
        {items.map((item, itemIndex) => (
          <BreadcrumbItem
            key={`${item.text}-${itemIndex}`}
            item={item}
          />
        ))}
      </div>
    </div>
  );
};

Breadcrumbs.displayName = 'Breadcrumbs';

Breadcrumbs.propTypes = {
  collectionId: PropTypes.string,
  shouldLastCrumbBeLinkableOverride: PropTypes.bool,
  itemId: PropTypes.string,
  keyword: PropTypes.string,
  navParam: PropTypes.string,
  rounded: PropTypes.bool,
  showInternet: PropTypes.bool,
  data: PropTypes.shape({}),
  loading: PropTypes.bool,
  shouldShowEventOnly: PropTypes.bool,
  staticCrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ),
  componentId: PropTypes.string,
  componentClass: PropTypes.string,
  componentPosition: PropTypes.number
};

const BreadCrumbType = shape({
  browseUrl: string(),
  creativeIconUrl: string(),
  deselectUrl: string(),
  dimensionName: string(),
  label: string(),
  refinementKey: string(),
  url: string()
});

Breadcrumbs.dataModel = {
  collection: params({
    collectionId: string().isRequired()
  }).shape({
    taxonomy: shape({
      breadCrumbs: arrayOf(shape({
        label: string(),
        url: string()
      }))
    })
  }),
  product: params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    taxonomy: shape({
      breadCrumbs: arrayOf(BreadCrumbType)
    })
  }),
  searchModel: params({ keyword: string(), navParam: string() }).shape({
    taxonomy: shape({
      breadCrumbs: arrayOf(BreadCrumbType)
    }),
    searchReport: shape({
      keyword: string()
    })
  })
};

export { Breadcrumbs };